import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Newspaper, ExternalLink } from "lucide-react";
import { useNewsItems } from "@/knowmeno/news/newsQueries";

export const Route = createFileRoute("/news")({
  head: () => ({
    meta: [
      { title: "Menopause News & Research — KnowMeno" },
      {
        name: "description",
        content:
          "Curated menopause news from trusted sources — peer-reviewed research, treatment updates, and products helping women cope with symptoms.",
      },
      { property: "og:title", content: "Menopause News — KnowMeno" },
      {
        property: "og:description",
        content:
          "Latest research, therapies and product updates for women navigating perimenopause and beyond.",
      },
      { property: "og:url", content: "https://knowmeno.com/news" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://knowmeno.com/og-image.png?v=2" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://knowmeno.com/news" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Menopause News",
          description:
            "Curated menopause news from trusted sources — research, treatments, and products for perimenopause and beyond.",
          url: "https://knowmeno.com/news",
          isPartOf: {
            "@type": "WebSite",
            name: "KnowMeno",
            url: "https://knowmeno.com",
          },
          about: { "@type": "Thing", name: "Menopause news and research" },
        }),
      },
    ],
  }),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
  errorComponent: () => <div className="p-8">Something went wrong loading the news.</div>,
  component: NewsPage,
});

const TOPICS = ["all", "research", "treatment", "product", "lifestyle"] as const;
type Topic = (typeof TOPICS)[number];

function NewsPage() {
  const { items, loading } = useNewsItems(100);
  const [topic, setTopic] = useState<Topic>("all");

  const filtered = topic === "all" ? items : items.filter((i) => i.topic === topic);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to KnowMeno
          </Link>
          <div className="flex items-center gap-2 font-serif text-xl">
            <Newspaper className="h-5 w-5" /> Meno News
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-4 pt-8 pb-4">
        <h1 className="font-serif text-3xl sm:text-4xl">Menopause news worth reading</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Curated from peer-reviewed journals and trusted medical sources. We filter
          out the noise so you don't have to.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t)}
              className={`rounded-full border px-3 py-1 text-sm capitalize transition ${
                topic === t
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:bg-muted"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-16">
        {loading && (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No items yet. Check back soon — we refresh several times a day.
          </div>
        )}

        <ul className="divide-y divide-border">
          {filtered.map((it) => {
            const isAffiliate = !!it.affiliate_url;
            const href = it.affiliate_url || it.source_url;
            return (
              <li key={it.id} className="py-5">
                <div className="flex items-start gap-4">
                  {it.image_url && (
                    <img
                      src={it.image_url}
                      alt=""
                      width={112}
                      height={80}
                      loading="lazy"
                      decoding="async"
                      className="hidden h-20 w-28 shrink-0 rounded object-cover sm:block"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded bg-muted px-2 py-0.5 capitalize">{it.topic}</span>
                      <span>{it.source_name}</span>
                      {it.published_at && (
                        <span>· {new Date(it.published_at).toLocaleDateString()}</span>
                      )}
                      {isAffiliate && (
                        <span className="rounded bg-primary/10 px-2 py-0.5 font-semibold uppercase text-primary">
                          Shop
                        </span>
                      )}
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel={
                        isAffiliate
                          ? "nofollow sponsored noopener noreferrer"
                          : "noopener noreferrer"
                      }
                      className="group inline-flex items-start gap-1 text-base font-semibold hover:text-primary"
                    >
                      {it.title}
                      <ExternalLink className="mt-1 h-3.5 w-3.5 opacity-50 group-hover:opacity-100" />
                    </a>
                    {it.summary && (
                      <p className="mt-1 text-sm text-muted-foreground">{it.summary}</p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="mt-10 text-xs text-muted-foreground">
          KnowMeno is not a medical provider. News links go to original sources. Some
          product links are affiliate links and may earn us a small commission at no
          cost to you.
        </p>
      </section>
    </div>
  );
}
